/**
 * One writer for the save across tabs and windows (FINISH_PLAN WP4.1). Two tabs of the game would each hold their own
 * copy of the save in memory and overwrite each other's progress on every save, so the newest tab takes the save over
 * and every older one stops writing and says so (features/platform.ts shows "open in another tab — PLAY HERE", which
 * reloads it as the newest). BroadcastChannel carries the claim; without it (Safari before 15.4) every tab writes, as
 * before.
 */

/** The messages on the channel. */
type LockMessage =
  | { readonly type: "claim"; readonly tab: string }
  /** The old writer has flushed its last write and stopped: the claimer may read the save now. */
  | { readonly type: "released"; readonly tab: string; readonly to: string };

/** The part of BroadcastChannel the lock uses (tests pass a fake). */
export interface LockChannel {
  postMessage(msg: unknown): void;
  onmessage: ((ev: { data: unknown }) => void) | null;
  close(): void;
}

export class WriterLock {
  static readonly ChannelName = "highmarch.save-writer";
  /**
   * How long a new tab waits for an older writer to flush before reading the save anyway. The mirror is written
   * synchronously, so the wait only covers a backend write in flight; a tab that is frozen in the background (iOS)
   * never answers.
   */
  static readonly ReleaseWaitMs = 300;

  private writer = true;
  /** This tab's own claim is still waiting for a release: a claim from another tab meanwhile is a tie. */
  private claiming = false;
  private readonly lostListeners: Array<() => void> = [];
  private releaseWaiter: (() => void) | null = null;

  /**
   * @param channel null: no BroadcastChannel, so this tab is always the writer.
   * @param flush resolves when this tab's pending writes have landed (the store's `flush`), before it answers a claim.
   */
  constructor(private readonly channel: LockChannel | null, readonly tab: string, private flush: () => Promise<void> = async () => undefined) {
    if (channel) channel.onmessage = (ev) => this.onMessage(ev.data);
  }

  /** The browser's channel, or null where BroadcastChannel is missing. */
  static browserChannel(): LockChannel | null {
    try {
      return typeof BroadcastChannel === "function" ? (new BroadcastChannel(WriterLock.ChannelName) as unknown as LockChannel) : null;
    } catch { return null; }
  }

  /** Whether this tab may write the save. */
  get isWriter(): boolean { return this.writer; }

  /** Called once when another tab takes the save over. */
  onLost(listener: () => void): void { this.lostListeners.push(listener); }

  /** The store's flush, set once the store exists (the lock is created before it). */
  setFlush(flush: () => Promise<void>): void { this.flush = flush; }

  /** Takes the save over: resolves once an older writer has released it, or after `ReleaseWaitMs`. */
  claim(wait: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms))): Promise<void> {
    this.writer = true;
    if (!this.channel) return Promise.resolve();
    this.claiming = true;
    const released = new Promise<void>((resolve) => { this.releaseWaiter = resolve; });
    this.channel.postMessage({ type: "claim", tab: this.tab } satisfies LockMessage);
    return Promise.race([released, wait(WriterLock.ReleaseWaitMs)]).then(() => { this.releaseWaiter = null; this.claiming = false; });
  }

  dispose(): void { this.channel?.close(); }

  private onMessage(data: unknown): void {
    const msg = data as Partial<LockMessage> | null;
    if (!msg || typeof msg.tab !== "string" || msg.tab === this.tab) return;
    const from = msg.tab;
    // Two tabs that claim at the same moment (a browser restoring a session) each receive the other's claim: had both
    // yielded, neither would save until PLAY HERE. The lower tab id keeps the save; the other yields as usual.
    if (msg.type === "claim" && this.writer && this.claiming && this.tab < from) return;
    if (msg.type === "claim" && this.writer) {
      this.writer = false;
      for (const l of this.lostListeners) l();
      void this.flush().catch(() => undefined).then(() => this.channel?.postMessage({ type: "released", tab: this.tab, to: from } satisfies LockMessage));
    } else if (msg.type === "released" && (msg as { to?: string }).to === this.tab) {
      this.releaseWaiter?.();
    }
  }
}
